import { RowData } from "../views/YouTubeScorecard/Interfaces/interfaces";
import { getPrivacy } from "../views/YouTubeScorecard/Components/FilterBars/auditFilters";

export const getMyIssuesRows = (
  rows: RowData[],
  loggedInUserEmail?: string
): RowData[] => {
  if (!loggedInUserEmail) return [];

  const me = loggedInUserEmail.trim().toLowerCase();
  return rows.filter((row) => {
    const userEmails =
      row.ChannelManagerEmail?.split(",").map((email) => email.trim().toLowerCase()) || [];
    return userEmails.includes(me);
  });
};

type FilterOptions = Record<string, (row: RowData) => boolean>;

interface FilterYouTubeScorecardRowsArgs {
  rows: RowData[];
  filters: any;
  filterOptions: FilterOptions;
  myIssuesOnly: boolean;
  myIssuesRows: RowData[];
  loggedInUserEmail?: string;
}

export const filterYouTubeScorecardRows = ({
  rows,
  filters,
  filterOptions,
  myIssuesOnly,
  myIssuesRows,
  loggedInUserEmail,
}: FilterYouTubeScorecardRowsArgs): RowData[] => {
  let filteredRows = rows;

  if (myIssuesOnly && loggedInUserEmail) {
    filteredRows = myIssuesRows;
  }

  // Apply the rest of the filters
  return filteredRows.filter((row) => {
    // Video ID filter
    if (filters.selectedVideoID && filters.selectedVideoID.length > 0) {
      if (!filters.selectedVideoID.includes(row.videoId)) {
        return false;
      }
    }

    // CMS Filter
    if (filters.selectedCMS && filters.selectedCMS.length > 0) {
      if (!filters.selectedCMS.includes(row.CMS)) {
        return false;
      }
    }

    // Channel Filter (Name or ID)
    // Handle TreeSelect channel filter (selectedChannels is now an object)
    if (filters.selectedChannels && Object.keys(filters.selectedChannels).length > 0) {
      // Extract selected channel names from the tree structure
      const selectedChannelNames: string[] = [];

      for (const key in filters.selectedChannels) {
        if (filters.selectedChannels[key]) {
          // Check if this is a leaf node (channel node)
          // Leaf nodes have the format: "channel___ChannelName"
          if (key.startsWith("channel___")) {
            const channelName = key.replace("channel___", "");
            selectedChannelNames.push(channelName);
          }
        }
      }

      // If channels are selected, check if the row matches
      if (selectedChannelNames.length > 0) {
        if (!selectedChannelNames.includes(row.Channel_Name)) {
          return false;
        }
      }
    }

    // Channel ID Filter
    if (filters.selectedChannelID && filters.selectedChannelID.length > 0) {
      if (!filters.selectedChannelID.includes(row.YoutubeChannelId)) {
        return false;
      }
    }

    // Channel Manager Filter
    if (
      filters.selectedChannelManagers &&
      filters.selectedChannelManagers.length > 0
    ) {
      const rowManagerEmails = (row.ChannelManagerEmail || "")
        .split(",")
        .map((email) => email.trim().toLowerCase())
        .filter((email) => email);

      if (
        !filters.selectedChannelManagers.some((selectedEmail: string) =>
          rowManagerEmails.includes(selectedEmail.toLowerCase())
        )
      ) {
        return false;
      }
    }

    // Content Format filter
    if (filters.selectedContentFormat && filters.selectedContentFormat.length > 0) {
      if (!filters.selectedContentFormat.includes(row.CreatorContentType)) {
        return false;
      }
    }

    // Privacy Status filter
    if (filters.selectedPrivacyStatus && filters.selectedPrivacyStatus.length > 0) {
      if (!filters.selectedPrivacyStatus.includes(getPrivacy(row))) {
        return false;
      }
    }

    // Legal Entity filter
    if (filters.selectedLegalEntity && filters.selectedLegalEntity.length > 0) {
      if (!filters.selectedLegalEntity.includes(row.LegalEntity)) {
        return false;
      }
    }

    // Legal Entity Type filter
    if (
      filters.selectedLegalEntityType &&
      filters.selectedLegalEntityType.length > 0
    ) {
      if (!filters.selectedLegalEntityType.includes(row.LegalEntityType)) {
        return false;
      }
    }

    // Auto-generated breaks filter (for Claims & Ads)
    if (
      filters.selected_auto_generated_breaks &&
      filters.selected_auto_generated_breaks.length > 0
    ) {
      if (!filters.selected_auto_generated_breaks.includes(row.auto_generated_breaks)) {
        return false;
      }
    }

    // "Select Issue" TreeSelect filter
    if (filters.selectedFilters && Object.keys(filters.selectedFilters).length > 0) {
      let issueMatch = false;
      for (const key in filters.selectedFilters) {
        if (filters.selectedFilters[key]) {
          // Apply issue-specific filtering based on filterOptions
          if (filterOptions && filterOptions[key]) {
            issueMatch = filterOptions[key](row);
          }
          if (issueMatch) break;
        }
      }
      if (!issueMatch && Object.values(filters.selectedFilters).some((v) => v)) {
        return false;
      }
    }

    row.privacy = getPrivacy(row);
    return true;
  });
};
